import React, { useState, useEffect, useCallback, useRef } from 'react';
import DataGrid, {
    Column,
    Paging,
    Pager,
    LoadPanel,
    Scrolling
} from 'devextreme-react/data-grid';
import './AnteprimaFiltro.css';

interface AnteprimaFiltroProps {
    dataSource: any;
    showPreview: boolean;
}

const AnteprimaFiltro: React.FC<AnteprimaFiltroProps> = ({ dataSource, showPreview }) => {
    // Stato per tenere traccia se i dati sono in caricamento
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Stato per tenere traccia se ci sono dati disponibili
    const [hasData, setHasData] = useState<boolean>(false);
    // Stato per tenere traccia degli errori
    const [error, setError] = useState<string | null>(null);
    // Riferimento per tracciare se il contenuto è già stato caricato
    const contentReadyHandled = useRef<boolean>(false);
    // Riferimento per tenere traccia dell'ultima chiamata dataSource
    const lastDataSourceRef = useRef<any>(null);

    // Reset dello stato quando cambia il dataSource
    useEffect(() => {
        // Se il dataSource è cambiato, resettiamo il flag
        if (dataSource !== lastDataSourceRef.current) {
            contentReadyHandled.current = false;
            lastDataSourceRef.current = dataSource;
        }
    }, [dataSource]);

    // Carica i dati quando cambia il dataSource o showPreview
    useEffect(() => {
        if (dataSource && showPreview) {
            setIsLoading(true);
            setError(null);

            // Carica i dati dal dataSource
            dataSource.load()
                .then((data: any) => {
                    // console.log('Dati anteprima caricati:', data);
                    setHasData(Array.isArray(data) && data.length > 0);
                    setIsLoading(false);
                })
                .catch((err: any) => {
                    console.error('Errore nel caricamento dei dati di anteprima:', err);
                    setError(err.message || 'Errore nel caricamento dei dati');
                    setHasData(false);
                    setIsLoading(false);
                });
        } else {
            setHasData(false);
            setIsLoading(false);
        }
    }, [dataSource, showPreview]);

    // Gestisce il completamento del caricamento dei dati - evitando chiamate ripetute
    const handleContentReady = useCallback((e: any) => {
        // Utilizziamo un ref per controllare se abbiamo già gestito questo evento
        if (contentReadyHandled.current || !e || !e.component) {
            return;
        }

        try {
            const totalCount = e.component.totalCount();
            // console.log('Totale record nell\'anteprima:', totalCount);
            setHasData(totalCount > 0);

            // Imposta il flag per evitare chiamate ripetute
            contentReadyHandled.current = true;
        } catch (error) {
            console.error('Errore durante l\'accesso ai dati totali:', error);
        }
    }, []);

    // Messaggio da visualizzare in base allo stato
    const getNoDataMessage = () => {
        if (error) {
            return `Errore: ${error}`;
        }

        if (!showPreview) {
            return "Nessun dato disponibile. Seleziona un filtro o creane uno nuovo.";
        }

        if (isLoading) {
            return "Caricamento dei dati in corso...";
        }

        return "Nessun dato disponibile per questo filtro.";
    };

    return (
        <div className="anteprima-filtro-container">
            <h3>Anteprima risultati</h3>

            <DataGrid
                dataSource={dataSource}
                // keyExpr="assistitoId"
                showBorders
                height="100%"
                columnAutoWidth
                columnHidingEnabled
                customizeColumns={(columns) => {
                    // Nascondiamo le colonne assistitoId e tenantId (indipendentemente dalla capitalizzazione)
                    columns.forEach(column => {
                        if (column.dataField) {
                            const fieldName = column.dataField.toLowerCase();
                            if (fieldName === 'tenantid' || fieldName === 'assistitoid') {
                                column.visible = false;
                            }
                        }
                    });
                }}
                noDataText={getNoDataMessage()}
                onContentReady={handleContentReady}
                className={isLoading ? 'loading-grid' : ''}
            >
                <LoadPanel enabled={true} showPane={true} showIndicator={true} />
                <Scrolling mode="virtual" rowRenderingMode="virtual" />
                <Paging defaultPageSize={10} />
                <Pager
                    showPageSizeSelector={true}
                    allowedPageSizes={[5, 10, 20]}
                    showInfo={true}
                />
            </DataGrid>

            {showPreview && hasData && !isLoading && (
                <div className="anteprima-info">
                    <p>I dati mostrati sono un'anteprima filtrata in base ai criteri selezionati.</p>
                </div>
            )}
        </div>
    );
};

export default React.memo(AnteprimaFiltro);
